/**
 * v4.9.0 — MyTap activation page.
 *
 * Mobile-first password setup for newly-invited employees. The activation
 * link sent by the manager points here with a JWT token in the URL.
 *
 * URL: /mytap/activate?token=<jwt>&lang=<en|fr|es>
 *
 * UX is intentionally consumer-grade simple:
 *   - language selector (chip row)
 *   - password input with show/hide
 *   - confirm field
 *   - strong-enough feedback strip (length only — no policy theatre)
 *   - "Activate MyTap" button
 *   - calm success state
 *   - expired / used link state with "Request new link" guidance
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff, CheckCircle2, AlertTriangle } from "lucide-react";

// Runtime origin — same pattern as `lib/api.js`. Avoids CRA baking a stale
// `REACT_APP_BACKEND_URL` (preview URL) into the production bundle, which
// previously broke /api/mytap/activate with CORS failures on portal.yulontap.com.
const BACKEND = typeof window !== "undefined" ? window.location.origin : "";
const LANGS = [
    { code: "en", label: "English" },
    { code: "fr", label: "Français" },
    { code: "es", label: "Español" },
];

const MyTapActivation = () => {
    const { t, i18n } = useTranslation();
    const [params] = useSearchParams();
    const nav = useNavigate();
    const token = params.get("token") || "";
    const urlLang = params.get("lang") || "en";
    const [pwd, setPwd] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [, forceUpdate] = useState(0); // Force re-render on language change

    // One-shot URL-param bootstrap. Runs once on mount; never reverts user clicks afterwards.
    const [bootstrappedUrlLang, setBootstrappedUrlLang] = useState(false);
    useEffect(() => {
        if (bootstrappedUrlLang) return;
        if (urlLang && ["en", "fr", "es"].includes(urlLang) && urlLang !== i18n.language) {
            i18n.changeLanguage(urlLang);
            try { localStorage.setItem("i18nextLng", urlLang); } catch { /* ignore */ }
        }
        setBootstrappedUrlLang(true);
    }, [urlLang, i18n, bootstrappedUrlLang]);

    // Subscribe to languageChanged so the chip pill + t() output update reliably.
    useEffect(() => {
        const onChange = () => forceUpdate(n => n + 1);
        i18n.on("languageChanged", onChange);
        return () => { i18n.off("languageChanged", onChange); };
    }, [i18n]);

    const handleLangChange = (code) => {
        try { localStorage.setItem("i18nextLng", code); } catch { /* ignore */ }
        i18n.changeLanguage(code).then(() => forceUpdate(n => n + 1));
    };

    const strong = pwd.length >= 8;
    const matches = pwd && pwd === confirm;
    const canSubmit = strong && matches && token && !submitting;

    const submit = async (e) => {
        e?.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setError(null);
        try {
            await axios.post(`${BACKEND}/api/mytap/activate`, {
                token, password: pwd, language: i18n.language || "en",
            });
            setSuccess(true);
        } catch (err) {
            const detail = err?.response?.data?.detail || "unknown_error";
            setError(detail);
        } finally { setSubmitting(false); }
    };

    return (
        <div
            data-testid="mytap-activation-page"
            className="min-h-screen flex items-center justify-center p-4"
            style={{ background: "linear-gradient(180deg,#0F172A 0%,#0B1018 100%)" }}
        >
            <div
                className="w-full max-w-md rounded-3xl p-8 sm:p-10"
                style={{ background: "rgba(15,23,42,0.85)", border: "1px solid rgba(255,255,255,0.08)",
                    boxShadow: "0 30px 80px rgba(0,0,0,0.45)" }}
            >
                {/* language selector */}
                <div className="flex gap-1.5 justify-center mb-8" data-testid="mytap-language-row">
                    {LANGS.map(l => (
                        <button
                            key={l.code}
                            type="button"
                            onClick={() => handleLangChange(l.code)}
                            data-testid={`mytap-lang-${l.code}`}
                            className="text-xs px-2.5 py-1 rounded-full transition-colors"
                            style={{
                                background: i18n.language?.startsWith(l.code) ? "rgba(167,243,208,0.18)" : "rgba(255,255,255,0.04)",
                                color: i18n.language?.startsWith(l.code) ? "#A7F3D0" : "#94A3B8",
                                border: `1px solid ${i18n.language?.startsWith(l.code) ? "rgba(16,185,129,0.32)" : "rgba(255,255,255,0.08)"}`,
                            }}
                        >{l.label}</button>
                    ))}
                </div>

                {/* SUCCESS STATE */}
                {success && (
                    <div className="text-center" data-testid="mytap-success-state">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                             style={{ background: "rgba(16,185,129,0.16)", border: "1px solid rgba(16,185,129,0.35)" }}>
                            <CheckCircle2 size={32} strokeWidth={1.5} color="#A7F3D0" />
                        </div>
                        <h1 className="text-2xl text-white mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {t("mytap.activate.success_title")}
                        </h1>
                        <p className="text-sm text-slate-400 mb-8">{t("mytap.activate.success_subtitle")}</p>
                        <button
                            onClick={() => nav("/login")}
                            className="inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm w-full"
                            style={{ background: "linear-gradient(135deg,#A7F3D0,#6EE7B7)", color: "#0B1018", fontWeight: 500 }}
                            data-testid="mytap-success-go-login"
                        >
                            {t("mytap.activate.go_to_signin")}
                        </button>
                    </div>
                )}

                {/* ERROR STATE — non-recoverable */}
                {!success && (error === "activation_link_expired" || error === "activation_link_used" || error === "invalid_activation_link") && (
                    <div className="text-center" data-testid="mytap-expired-state">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                             style={{ background: "rgba(245,158,11,0.14)", border: "1px solid rgba(245,158,11,0.32)" }}>
                            <AlertTriangle size={32} strokeWidth={1.5} color="#FBBF24" />
                        </div>
                        <h1 className="text-2xl text-white mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {t("mytap.activate.expired_title")}
                        </h1>
                        <p className="text-sm text-slate-400">{t("mytap.activate.expired_subtitle")}</p>
                    </div>
                )}

                {/* FORM */}
                {!success && !(error === "activation_link_expired" || error === "activation_link_used" || error === "invalid_activation_link") && (
                    <form onSubmit={submit} data-testid="mytap-activate-form">
                        <h1 className="text-2xl sm:text-3xl text-white mb-2 text-center" style={{ fontFamily: "Outfit, sans-serif" }}>
                            {t("mytap.activate.title")}
                        </h1>
                        <p className="text-sm text-slate-400 mb-8 text-center">{t("mytap.activate.subtitle")}</p>

                        <label className="block text-xs uppercase tracking-wider mb-2 text-slate-400">
                            {t("mytap.activate.password_label")}
                        </label>
                        <div className="relative mb-3">
                            <input
                                type={show ? "text" : "password"}
                                value={pwd}
                                onChange={(e) => setPwd(e.target.value)}
                                className="w-full rounded-xl px-4 py-3 text-base text-white outline-none"
                                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)" }}
                                placeholder={t("mytap.activate.password_placeholder")}
                                autoComplete="new-password"
                                autoFocus
                                data-testid="mytap-password-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShow(v => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-1"
                                aria-label={show ? "hide password" : "show password"}
                                data-testid="mytap-password-toggle"
                            >
                                {show ? <EyeOff size={16} color="#94A3B8" /> : <Eye size={16} color="#94A3B8" />}
                            </button>
                        </div>
                        <p className="text-xs text-slate-500 mb-5">{t("mytap.activate.password_helper")}</p>

                        <label className="block text-xs uppercase tracking-wider mb-2 text-slate-400">
                            {t("mytap.activate.confirm_label")}
                        </label>
                        <input
                            type={show ? "text" : "password"}
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            className="w-full rounded-xl px-4 py-3 text-base text-white outline-none mb-3"
                            style={{ background: "rgba(255,255,255,0.04)",
                                    border: `1px solid ${(!confirm || matches) ? "rgba(255,255,255,0.10)" : "rgba(248,113,113,0.35)"}` }}
                            placeholder={t("mytap.activate.confirm_placeholder")}
                            autoComplete="new-password"
                            data-testid="mytap-confirm-input"
                        />

                        {error && !["activation_link_expired", "activation_link_used", "invalid_activation_link"].includes(error) && (
                            <p className="text-xs text-red-400 mb-3" data-testid="mytap-error-msg">{t(`mytap.activate.error.${error}`, t("mytap.activate.error.unknown"))}</p>
                        )}

                        <button
                            type="submit"
                            disabled={!canSubmit}
                            data-testid="mytap-submit-btn"
                            className="w-full rounded-full px-6 py-3 text-sm transition-opacity"
                            style={{
                                background: "linear-gradient(135deg,#A7F3D0,#6EE7B7)",
                                color: "#0B1018",
                                fontWeight: 500,
                                opacity: canSubmit ? 1 : 0.4,
                                fontFamily: "Outfit, sans-serif",
                            }}
                        >
                            {submitting ? t("mytap.activate.activating") : t("mytap.activate.activate_button")}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

export default MyTapActivation;
